import { useCallback, useEffect, useState } from "react";

/**
 * useLocalStorage — minimal SSR-safe persistent state hook.
 *
 * Stores JSON-serialisable values under the given key. Reads lazily on
 * mount, falls back to `initial` if the stored value is missing or
 * corrupt. Writes both to React state and to localStorage so external
 * consumers (e.g. the workstation accent CSS swap) can observe via the
 * `storage` event too.
 */
export function useLocalStorage<T>(
  key: string,
  initial: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValueState] = useState<T>(() => readStorage<T>(key, initial));

  useEffect(() => {
    // Cross-tab sync: when the same key changes elsewhere, mirror locally.
    const handler = (event: StorageEvent) => {
      if (event.key !== key || event.newValue === null) return;
      try {
        setValueState(JSON.parse(event.newValue));
      } catch {
        // silent: malformed JSON in stored/streamed payload
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [key]);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved =
          typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // silent: localStorage unavailable (private mode / quota)
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setValue];
}

function readStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
